import { IAppointmentScheduleResponse, IPrescription } from "@curefit/care-common";
import IGenericPlanPrescriptionDetailObject from "../models/gms/IGenericPrescriptionDetailObject";
import IAccountData from "../models/IAccountData";
import { IEditConsultationResponse } from "../models/IEditConsultationResponse";
import { IOngoingConsultation } from "../models/IOngoingConsultation";
import IOrionPlanResponse from "../models/orion/IOrionPlanResponse";
import IPatientLifestylePlanWrapper from "../models/patients/IPatientLifestylePlanWrapper";
import IPlanStaticData from "../models/staticData/IPlanStaticData";
import { CONSULTATION_ID_PARAM } from "../routes";
import ApiUtils from "../utils/ApiUtils";
import { ConsultationStatusConstant } from "../utils/AppConstants";
import AppUtils from "../utils/AppUtils";
import { APPOINTMENT_ID_PARAM_CONST } from "./AlbusUrlConfigs";
import { IOrionApis } from "./IOrionApis";
import { FormTypes } from "../models/UserForm";
import { UserFormData } from "../reducers/UserformReducer";

const accountUrl: string = "/account";
const planStaticDataUrl: string = "/orion/static";
const unsavedPrescriptionUrl: string = "/orion/prescription/getUnsavedPrescription?consultationId=$appointmentId";
const savePrescriptionUrl: string = "/orion/prescription/savePrescription";
const removePrescriptionUrl: string = "/orion/prescription/removeUnsavedPrescription?consultationId=$consultationId";
const savePatientPlanInDraftUrl: string = "/orion/plan/savePatientPlanInDraft";
const getPatientPlanUrl: string = "/orion/plan/getPatientPlan?patientId=$patientId&secondaryUserId=$secondaryUserId&consultationId=$appointmentId&isSelf=$iSelfParam";
const orionLoggerUrl: string = "/orion/client/logger";
const fetchAdherenceUrl: string = "/orion/plan/adherence";
const fetchAllMPPatientsForDoctor: string = "/orion/patientInfo/getMPPatientsForDoctor";
const getOngoingConsultationDetailsUrl: string = "/orion/consultation/ongoing/fetch?patientId=$patientId&userId=$userId&appointmentId=$appointmentId&isSelf=$isSelfParam&isPresctionRequired=$isPresctionRequiredParam&isPackInfoRequired=$isPackInfoRequired";
const getEditConsultationDetailsUrl: string = "/orion/consultation/edit/fetch?patientId=$patientId&userId=$userId&appointmentId=$appointmentId&isSelf=$isSelfParam";
const savePatientPlanNewUrl: string = "/orion/consultation/ongoing/end";
const removeWorkflowObjectUrl: string = "/orion/consultation/ongoing/removeWorkflowObject";
const patientLifestylePlanViewUrl: string = "/orion/patientInfo/lifestylePlanView";
const staticDietRecallDataUrl: string = "/orion/static/fetch";
const adherenceOverallScoresUrl: string = "/orion/patientInfo/adherence/overallScores";
const updateAppointmentStatus: string = "/orion/consultation/update";
const getUserVitalsData: string = "/orion/patientInfo/getUserVitalsData";
const updatePatientPlanEligibilityMasks: string = "/orion/plan/updatePatientPlanEligibilityMasks"
const getCurrentTime: string = "/orion/utils/current-time";
const deleteDiagnosticLabTests: string = "/orion/patientInfo/deletePatientLabTests"
const requestPdfGeneration: string = "/orion/pdf/request-generation";
const fetchPastConsultationDataForPatientUrl: string = "/orion/consultation/pastConsultationData";
const getTreatmentPlanDetailsUrl: string = "/orion/consultation/treatmentPlan/fetch?doctorType=$doctorType";
const getUserFormUrl: string = "/orion/userform/$formId?userId=$userId";
const sendUserFormUrl: string = "/orion/userform/submit/$responseId?userId=$userId";
const getAllowedAgentTypesForAutomatedWorkingHours = "/orion/automated-working-hours/allowed-agent-types";
const logLogoutStatus = "/orion/utils/log";
const fetchAllExercises = '/orion/v1/microcycle/exercises';

export interface IAutoSavePrescriptionReq {
    id: number,
    prescription: IPrescription
}

interface IConsultationDataResponse {
    caseSummary: string,
    medicalProfile: any
}

export default class OrionApis implements IOrionApis {

    getAccountData(): Promise<IAccountData> {
        return ApiUtils.apiGetRequest(ApiUtils.createApiUrl(accountUrl));
    }

    getUserForm(formId: FormTypes, userId: string): Promise<UserFormData> {
        return ApiUtils.apiGetRequest(ApiUtils.createApiUrl(getUserFormUrl.replace("$formId", formId)).replace("$userId", userId));
    }

    sendFormData(formId: FormTypes, data: any, userId: string): Promise<any> {
        const id = data._id;
        return ApiUtils.apiPostRequest(sendUserFormUrl.replace("$responseId", id).replace("$userId", userId), JSON.stringify(data));
    }

    getUnsavedPrescription(consultationId: number): Promise<IPrescription> {
        return ApiUtils.apiGetRequest(ApiUtils.createApiUrl(unsavedPrescriptionUrl.replace(APPOINTMENT_ID_PARAM_CONST, consultationId.toString())));
    }

    savePatientPlanInDraft(consultationId: number, patientPlan: IGenericPlanPrescriptionDetailObject[]): Promise<any> {
        let req = { consultationId, patientPlan };
        return ApiUtils.apiPostRequest(savePatientPlanInDraftUrl, JSON.stringify(req));
    }

    getPatientPlan(patientId: number, secondaryUserId: string, consultationId: number, isSelf: boolean): Promise<IOrionPlanResponse> {
        return ApiUtils.apiGetRequest(ApiUtils.createApiUrl(getPatientPlanUrl.replace("$patientId", patientId.toString())
            .replace("$secondaryUserId", secondaryUserId.toString())
            .replace("$appointmentId", AppUtils.isDefined(consultationId) ? consultationId.toString() : null))
            .replace("$iSelfParam", isSelf.toString()))
    }

    savePrescriptionInRedis(autoSavePrescriptionReq: IAutoSavePrescriptionReq): Promise<any> {
        return ApiUtils.apiPostRequest(savePrescriptionUrl, JSON.stringify(autoSavePrescriptionReq))
    }

    removePrescriptionFromRedis(consultationId: number): Promise<any> {
        return ApiUtils.apiGetRequest(removePrescriptionUrl.replace(`$${CONSULTATION_ID_PARAM}`, consultationId.toString()))
    }

    getPlanStaticData(): Promise<IPlanStaticData> {
        return ApiUtils.apiGetRequest(planStaticDataUrl);
    }

    getStaticDietRecallData(): Promise<any> {
        return ApiUtils.apiGetRequest(staticDietRecallDataUrl);
    }

    orionClientLogger(err: any): Promise<any> {
        return ApiUtils.apiPostRequest(orionLoggerUrl, JSON.stringify(err));
    }
    getPatientAdherenceDetails(params: any): Promise<object> {
        let currUrl = fetchAdherenceUrl + "?" + ApiUtils.requestToQueryParams(params);
        return ApiUtils.apiGetRequest(currUrl)
    }

    getAllMpPatientsForDoctor(doctorId: string, aggregateLevel: string, startEpoch: number, endEpoch: number): Promise<object> {
        const params = ApiUtils.requestToQueryParams({ doctorId, aggregateLevel, startEpoch, endEpoch });
        let url = `${fetchAllMPPatientsForDoctor}?${params}`;
        return ApiUtils.apiGetRequest(url);
    }

    getOngoingConsultationDetails(patientId: number, userId: number, appointmentId: number, isSelf: boolean, isPresctionRequired?: boolean, isPackInfoRequired?: boolean): Promise<IOngoingConsultation> {
        return ApiUtils.apiGetRequest(getOngoingConsultationDetailsUrl
            .replace("$patientId", patientId.toString())
            .replace("$userId", userId.toString())
            .replace("$appointmentId", appointmentId.toString())
            .replace("$isSelfParam", isSelf.toString())
            .replace("$isPresctionRequiredParam", isPresctionRequired ? isPresctionRequired.toString() : "false")
            .replace("$isPackInfoRequired", isPackInfoRequired.toString())
        )
    }

    getEditConsultationDetails(patientId: number, userId: number, appointmentId: number, isSelf: boolean): Promise<IEditConsultationResponse> {
        return ApiUtils.apiGetRequest(getEditConsultationDetailsUrl
            .replace("$patientId", patientId.toString())
            .replace("$userId", userId.toString())
            .replace("$appointmentId", appointmentId.toString())
            .replace("$isSelfParam", isSelf.toString())
        )
    }

    getTreatmentPlanDetails(doctorType: string): Promise<any> {
        return ApiUtils.apiGetRequest(getTreatmentPlanDetailsUrl
            .replace("$doctorType", doctorType)
        )
    }

    savePatientPlanNew(consultation: IAppointmentScheduleResponse, prescription: IPrescription, patientPlan: IPatientLifestylePlanWrapper, planId: string, status: ConsultationStatusConstant, isSelf: boolean, medicalProfile?: any): Promise<any> {
        let req = { consultation, prescription, patientPlan, planId, status, isSelf, medicalProfile };
        return ApiUtils.apiPostRequest(savePatientPlanNewUrl, JSON.stringify(req))
    }

    removePlanWorkflowObject(consultation: IAppointmentScheduleResponse, isPrescriptionObject: boolean): Promise<any> {
        let req = { consultation, isPrescriptionObject };
        return ApiUtils.apiPostRequest(removeWorkflowObjectUrl, JSON.stringify(req));
    }

    getPatientLifestylePlanView(secondaryUserId: string): Promise<any> {
        let params = ApiUtils.requestToQueryParams({ secondaryUserId });
        return ApiUtils.apiGetRequest(`${patientLifestylePlanViewUrl}?${params}`)
    }

    getPatientsOverallAdherenceScores(aggregateLevel: string, startEpoch: number, endEpoch: number, userIds: string): Promise<any> {
        let params = ApiUtils.requestToQueryParams({ aggregateLevel, startEpoch, endEpoch, userIds });
        return ApiUtils.apiGetRequest(`${adherenceOverallScoresUrl}?${params}`)
    }

    updateAppointmentStatus(bookingId: number, appointmentId: number, status: string): Promise<any> {
        let params = ApiUtils.requestToQueryParams({ bookingId, appointmentId, status })
        return ApiUtils.apiGetRequest(`${updateAppointmentStatus}?${params}`);
    }

    getCurrentTime(): Promise<any> {
        return ApiUtils.apiGetRequest(getCurrentTime);
    }

    fetchUserVitalsDataFromMetricService(userId: number, patientId: number, metricIds: number[]): Promise<any> {
        return ApiUtils.apiGetRequest(`${getUserVitalsData}?userId=${userId}&subUserId=${patientId}&metricIds=${metricIds}`);
    }
    updatePatientPlanEligibilityMasks(userId: String, secondaryUserId: String, isSelf: Boolean, eligibilityMasks: String[]): Promise<any> {
        let params = ApiUtils.requestToQueryParams({ userId, secondaryUserId, isSelf })
        let req = { eligibilityMasks };
        return ApiUtils.apiPostRequest(`${updatePatientPlanEligibilityMasks}?${params}`, JSON.stringify(req));
    }
    deletePatientDiagnosticLabTests(labTestIds: number[]): Promise<any> {
        return ApiUtils.apiDeleteRequest(deleteDiagnosticLabTests, JSON.stringify({ labTestIds: labTestIds }))
    }
    requestPdfGeneration(html: string, status: ConsultationStatusConstant, bookingId: number, consultationId: number, isPlan: boolean, sigUrl: string): Promise<any> {
        return ApiUtils.apiPostRequest(requestPdfGeneration, JSON.stringify({
            html,
            status,
            bookingId,
            consultationId,
            isPlan,
            sigUrl
        }))
    }

    fetchPastConsultationDataForPatient(consultationId: string, doctorType: string, patientId: string): Promise<IConsultationDataResponse> {
        const queryParams = ApiUtils.requestToQueryParams({
            consultationId,
            doctorType,
            patientId
        })
        return ApiUtils.apiGetRequest(`${fetchPastConsultationDataForPatientUrl}?${queryParams}`)
    }

    getAllowedAgentTypesForAutomatedWorkingHours() {
        return ApiUtils.apiGetRequest(getAllowedAgentTypesForAutomatedWorkingHours);
    }

    logLogoutStatus(details: string) {
        return ApiUtils.apiPostRequest(logLogoutStatus, details);
    }

    fetchAllExercises() {
        return ApiUtils.apiGetRequest(fetchAllExercises);
    }
}